import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Button, Alert, Tabs, Tab, CircularProgress } from '@mui/material';
import { PlayArrow, Refresh } from '@mui/icons-material';
import Editor from '@monaco-editor/react';

function VueWorkspace({ question, onCodeChange, initialCode = '' }) {
  const defaultCode = `<template>
  <div class="app">
    <h1>Vue Component</h1>
    <p>Count: {{ count }}</p>
    <button @click="increment">Increment</button>
  </div>
</template>

<script>
export default {
  data() {
    return {
      count: 0
    }
  },
  methods: {
    increment() {
      this.count++
    }
  }
}
</script>

<style scoped>
.app {
  padding: 20px;
  font-family: Arial, sans-serif;
}
button {
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
}
</style>`;

  const [code, setCode] = useState(initialCode || defaultCode);
  const [error, setError] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (onCodeChange) {
      onCodeChange(code);
    }
  }, [code, onCodeChange]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
  };

  const compileAndRun = async () => {
    setIsRunning(true);
    setError('');
    setActiveTab(1);

    try {
      // Parse Vue SFC
      const templateMatch = code.match(/<template>([\s\S]*?)<\/template>/);
      const scriptMatch = code.match(/<script>([\s\S]*?)<\/script>/);
      const styleMatch = code.match(/<style[^>]*>([\s\S]*?)<\/style>/);

      const template = templateMatch ? templateMatch[1].trim() : '<div>No template found</div>';
      const script = scriptMatch ? scriptMatch[1].trim() : 'export default {}';
      const styles = styleMatch ? styleMatch[1].trim() : '';

      // Create iframe content with Vue 3
      const iframeContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <script src="https://unpkg.com/vue@3/dist/vue.global.js"></script>
  <style>
    body { margin: 0; padding: 0; }
    ${styles}
  </style>
</head>
<body>
  <div id="app"></div>
  <script>
    try {
      const { createApp } = Vue;

      ${script.replace('export default', 'const component =')}

      component.template = \`${template}\`;

      createApp(component).mount('#app');
    } catch (err) {
      document.body.innerHTML = '<div style="padding: 20px; color: red;"><h3>Runtime Error:</h3><pre>' + err.toString() + '</pre></div>';
      console.error(err);
    }
  </script>
</body>
</html>`;

      // Update iframe
      if (iframeRef.current) {
        const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
        doc.open();
        doc.write(iframeContent);
        doc.close();
      }

      setPreviewKey(prev => prev + 1);
    } catch (err) {
      setError('Compilation Error: ' + err.message);
      console.error('Vue compilation error:', err);
    } finally {
      setIsRunning(false);
    }
  };

  const refresh = () => {
    setPreviewKey(prev => prev + 1);
    compileAndRun();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {question && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {question.name || 'Vue Challenge'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {question.task || question.question || question.text}
          </Typography>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Vue SFC Editor" />
        <Tab label="Live Preview" />
      </Tabs>

      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="html"
            path="App.vue"
            value={code}
            onChange={(value) => setCode(value || '')}
            onMount={handleEditorDidMount}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              automaticLayout: true,
              tabSize: 2,
            }}
          />
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', bgcolor: 'white' }}>
          <iframe
            key={previewKey}
            ref={iframeRef}
            title="Vue Preview"
            style={{ width: '100%', height: '100%', border: 'none' }}
            sandbox="allow-scripts"
          />
        </Box>
      )}

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={isRunning ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
          onClick={compileAndRun}
          disabled={isRunning}
        >
          {isRunning ? 'Compiling...' : 'Run & Preview'}
        </Button>
        <Button variant="outlined" startIcon={<Refresh />} onClick={refresh} disabled={isRunning}>
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Vue Workspace</strong> - Write Vue 3 Single File Components with template, script, and style sections.
      </Alert>
    </Box>
  );
}

export default VueWorkspace;
